import { useState, useEffect } from "react";
import PropTypes from 'prop-types';

// @mui material components
import { Grid, Tabs, Tab, Box, Card } from "@mui/material";
import { tabsClasses } from '@mui/material/Tabs';

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";

//Backend access
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useMatch, useLocation, Link } from 'react-router-dom';

import { useAuth0 } from "@auth0/auth0-react";

import bksAPI from "services/api/bks";
import groupAPI from "services/api/groups";
import sitesAPI from "services/api/sites";
import gtfsAPI from "services/api/gtfs";

import { setupDeviceTabSelectors } from "utils/setup";
import { wrapper, loadingScreen } from "utils/wrapper";
import { processDeviceData } from "utils/device";
import { useSoftUIController } from "hooks/context";

import DeviceOverviewTab from "pages/components/Devices/DeviceView/Overview";
import CameraFeedTab from "pages/components/Devices/DeviceView/CameraFeed";
import EventsTab from "pages/components/Devices/DeviceView/Events";
import StatisticsTab from "pages/components/Devices/DeviceView/Statistics";
import GenerateReportTab from "pages/components/Devices/DeviceView/GenerateReport";
import DeviceSettingsTab from "pages/components/Devices/DeviceView/Settings";
import { disableMap } from "utils/map";
import { deviceViewWrapper } from "utils/wrapper";
import SoftButton from "components/SoftComponents/SoftButton";
import LiveView from "components/LiveView";
import DeviceInspectionHistoryTab from "pages/components/Devices/DeviceView/History";
import { processGroupData } from "utils/group";
import { getUserAccessLevel } from "utils/setup";
import DeviceCampaignsTab from "pages/components/Devices/DeviceView/Campaigns";
import LiveviewTimeline from "components/Timelines/LiveviewTimeline";
import OccupancyFeed from "components/Camera/OccupancyFeed";
import AllCampaignsView from "./AllCampaignsView";

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ px: 1, py: 0, height: '100%' }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

function DeviceView({ map, user }) {
    useEffect(() => {
        if (!map.current) return;
        disableMap(map);
    }, []);

    bksAPI.loadToken(useAuth0());
    groupAPI.loadToken(useAuth0());
    sitesAPI.loadToken(useAuth0());
    gtfsAPI.loadToken(useAuth0());
    const [controller] = useSoftUIController();
    const { deviceLayout } = controller;

    const navigate = useNavigate();
    const { uuid } = useMatch('devices/:uuid').params;

    const [tabValue, setTabValue] = useState(0);
    const [isPerspectiveLiveview, setIsPerspectiveLiveview] = useState(true);
    const [directionTrigger, setDirectionTrigger] = useState({ prev: 0, next: 0 });

    const handleTabClick = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const device = useQuery({
        queryKey: ['getDevice', uuid],
        queryFn: async () => {
            return await bksAPI.getDevice(uuid);
        },
        refetchInterval: 30 * 1000
    });

    const sites = useQuery({
        queryKey: ['getAllSites'],
        queryFn: async () => {
            return await sitesAPI.getAllSites();
        }
    });

    const tags = useQuery({
        queryKey: ['getAllTags'],
        queryFn: async () => {
            return await gtfsAPI.getTags();
        }
    });


    const group = useQuery({
        queryKey: ['getGroupWithDeviceUUID', uuid],
        enabled: device.data && device.data.organisation_id == "clearways",
        queryFn: async () => {
            return await groupAPI.getGroupWithDeviceUUID(uuid);
        }
    });

    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            // Prevent arrows from scrolling the page
            if (e.keyCode === 37 || e.keyCode === 39) {
                if (e.keyCode === 39) { // Right
                    setDirectionTrigger(prev => ({ ...prev, next: prev.next + 1 }));
                } else if (e.keyCode === 37) { // Left
                    setDirectionTrigger(prev => ({ ...prev, prev: prev.prev + 1 }));
                }
            }
        };

        window.addEventListener("keydown", handleGlobalKeyDown);
        return () => window.removeEventListener("keydown", handleGlobalKeyDown);
    }, []);

    const isGroupLoading = device.data?.organisation_id === "clearways" && group.isLoading;

    if (device.isLoading || isGroupLoading || sites.isLoading) {
        return loadingScreen(deviceLayout, true);
    }



    if (device.error || group.error || sites.error) {
        return wrapper("An error has occurred fetching data", deviceLayout);
    }

    var group_data = group.data && device.data.organisation_id == "clearways" ? processGroupData(group.data, [device.data], getUserAccessLevel(user.user.user_type, 'technician')) : false;
    var device_data = processDeviceData(device.data, group_data);

    const tabGeneration = () => {
        var tabs = setupDeviceTabSelectors(user.user, device_data, group_data, deviceLayout);
        return tabs.map(function (row, index) {
            return <Tab key={index} sx={{ minWidth: "fit-content !important", padding: '12px !important' }} label={row} {...a11yProps(index)} />
        });
    }

    const liveviewInfo = (
        <>
            {(device_data.hardware_type == 'bk-camera' && device_data.organisation_id != "clearways") ? (
                <Box sx={{ height: deviceLayout != "mobile" && "calc(100svh - 10.25rem - 16px)" }}>
                    <OccupancyFeed device={device_data} user={user} deviceLayout={deviceLayout} sx={{ height: "100%" }} />
                </Box>
            ) : (
                <>
                    {(device_data.liveview_type == "history") ? (
                        <LiveviewTimeline uuid={uuid} device={device_data} deviceLayout={deviceLayout} isPerspectiveLiveview={(device_data?.overlay_id && device_data?.overlay_id != 'no_frame' && device_data.organisation_id != 'clearways') ? isPerspectiveLiveview : !isPerspectiveLiveview} directionTrigger={directionTrigger} />
                    ) : (
                        <Box display="flex" flex={1} flexDirection={{ xxs: "column" }} height="calc(100svh - 10.25rem - 16px)"> {/* Need to fix for mobile state */}
                            {(getUserAccessLevel(user.user.user_type, "technician") && device_data?.overlay_id && device_data.overlay_id != 'no_frame') ? (
                                <>
                                    <Box flex={{ xxs: 1 }} pb={1}>
                                        <SoftBox display="flex" justifyContent="center" alignItems="center" height="100%">
                                            {/* {getUserAccessLevel(user.user.user_type, "technician") && (
                                                <SoftButton component={Link} to={`/campaigns/${uuid}`} variant="gradient" color="light" circular={true} sx={{ fontSize: '13px', marginLeft: '8px', marginRight: '8px' }}>
                                                    Change Media
                                                </SoftButton>
                                            )} */}
                                            {(device_data?.overlay_id && device_data?.overlay_id != 'no_frame') && (
                                                <SoftButton variant="contained" color="dark" circular={true} onClick={() => { setIsPerspectiveLiveview(!isPerspectiveLiveview) }}
                                                    sx={{ fontSize: '13px', marginLeft: '8px', marginRight: '8px' }}>
                                                    Switch View
                                                </SoftButton>
                                            )}
                                        </SoftBox>
                                    </Box>
                                    <Box display="flex" flex={{ xxs: 11 }} sx={{ maxHeight: 'calc(100svh - 16rem - 16px)' }}>
                                        <LiveView device={device_data} nexus_id={user.organisations.find((org) => org.organisation_id == device_data.organisation_id).nexus_id || null} deviceLayout={deviceLayout} isPerspectiveLiveview={device_data?.overlay_id && device_data?.overlay_id != 'no_frame' ? isPerspectiveLiveview : !isPerspectiveLiveview} />
                                    </Box>
                                </>
                            ) : (
                                <Box display="flex" alignItems="center" flex={{ xxs: 12 }} sx={{ maxHeight: 'calc(100svh - 10.25rem - 16px)' }}>
                                    <LiveView device={device_data} nexus_id={user.organisations.find((org) => org.organisation_id == device_data.organisation_id).nexus_id || null} deviceLayout={deviceLayout} isPerspectiveLiveview={device_data?.overlay_id && device_data.overlay_id != 'no_frame' ? isPerspectiveLiveview : !isPerspectiveLiveview} />
                                </Box>
                            )}
                        </Box>
                    )}
                </>
            )}
        </>
    );

    return (
        wrapper((
            <SoftBox sx={{ overflow: "visible", backgroundColor: 'transparent', height: '100%' }}>
                <Grid container>
                    <Grid item xxs={12}>
                        <SoftBox p={1}>
                            <Tabs value={tabValue} onChange={handleTabClick} aria-label="DeviceView Tabs"
                                allowScrollButtonsMobile
                                variant="scrollable"
                                scrollButtons
                                sx={{
                                    [`& .${tabsClasses.scrollButtons}`]: {
                                        '&.Mui-disabled': { opacity: 0 },
                                    },
                                }}
                            >
                                {tabGeneration()}
                            </Tabs>
                        </SoftBox>
                    </Grid>
                </Grid>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(user.user, device_data, group_data, deviceLayout, "Overview")}>
                    {deviceLayout == 'mobile' ? (
                        <Box pb={1}>
                            <DeviceOverviewTab device={device_data} deviceLayout={deviceLayout} user_type={user.user.user_type} />
                        </Box>
                    ) : deviceViewWrapper(
                        <DeviceOverviewTab device={device_data} deviceLayout={deviceLayout} user_type={user.user.user_type} />,
                        liveviewInfo,
                        deviceLayout,
                        true
                    )}
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(user.user, device_data, group_data, deviceLayout, "LiveView")}>
                    <Box pb={1}>
                        {liveviewInfo}
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(user.user, device_data, group_data, deviceLayout, "Generate Report")}>
                    <GenerateReportTab {...{ uuid, deviceLayout }} />
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(user.user, device_data, group_data, deviceLayout, "Statistics")}>
                    <Box pb={1}>
                        <StatisticsTab {...{ uuid, deviceLayout }} />
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(user.user, device_data, group_data, deviceLayout, "Events")}>
                    <Box pb={1}>
                        <EventsTab {...{ uuid, deviceLayout }} />
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(user.user, device_data, group_data, deviceLayout, "Campaigns")}>
                    <Box pb={1}>
                        <AllCampaignsView isEmbedded={true} initialTags={tags?.data?.stops.filter(r => r.stop_id == device.data.site_id).map((r) => {
                            return { category: 'Stop', field: 'stop_id', value: r.stop_id, label: `Stop ${r.stop_id} - ${r.gtfs.stop_name}` }
                        })} />
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(user.user, device_data, group_data, deviceLayout, "History")}>
                    <Box pb={1}>
                        <DeviceInspectionHistoryTab user={user} device={device_data} deviceLayout={deviceLayout} />
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(user.user, device_data, group_data, deviceLayout, "Camera Feed")}>
                    <Box pb={1}>
                        <CameraFeedTab device={device_data} deviceLayout={deviceLayout} directionTrigger={directionTrigger} />
                    </Box>
                </CustomTabPanel>
                <CustomTabPanel value={tabValue} index={setupDeviceTabSelectors(user.user, device_data, group_data, deviceLayout, "Settings")}>
                    {deviceLayout == 'mobile' ? (
                        <Box pb={1}>
                            <DeviceSettingsTab uuid={uuid} device={device_data} tags={tags.data} sites={sites.data} raw={device.data} deviceLayout={deviceLayout} user_type={user.user.user_type} />
                        </Box>
                    ) : deviceViewWrapper(
                        <DeviceSettingsTab uuid={uuid} device={device_data} tags={tags.data} sites={sites.data} raw={device.data} deviceLayout={deviceLayout} user_type={user.user.user_type} />,
                        liveviewInfo,
                        deviceLayout
                    )}
                </CustomTabPanel>
            </SoftBox>
        ), deviceLayout, true
        )
    );
}

DeviceView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object
}

export default DeviceView;
